import {
  type ChangeEvent,
  type FormEvent,
  useMemo,
  useReducer,
} from "react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import {
  CreateManualCfrRequestValidation,
  type CreateManualCfrResult,
  type ListCfrTemplateItem,
  type ManualCfrStatus,
  useConfirmManualCfrSigningSessionUploads,
  useCreateManualCfrSigningSession,
  useListCfrTemplates,
  useSoftDeleteManualCfrSigningSession,
} from "@/lib/api/cfr";
import { CommodityOptions } from "@/lib/api/commodity";
import { useListCustomers } from "@/lib/api/customer";
import type { Customer } from "@/lib/api/reports";
import type { DocumentTemplate } from "@/lib/api/signingContracts";
import { MimeType } from "@/lib/api/utils";
import { UploadPresignedFile } from "@/lib/file";
import type { SelectOptionItem } from "@/lib/models/ui";
import {
  initialManualCfrUploadState,
  manualCfrUploadReducer,
} from "./manualCfrUploadReducer";
import { ManualCfrUploadFormValidation } from "./manualCfrUploadValidation";
import { getMessageOrDefault } from "@/lib/prelude";

export type ManualCfrUploadDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

type SelectableManualCfrTemplate = ListCfrTemplateItem & {
  customer_destination_id: string;
  DocumentTemplate: DocumentTemplate;
};

type UploadItem = {
  filename: string;
  file: File;
  presignedPutUrl: string;
};

const manualStatusOptions: SelectOptionItem<ManualCfrStatus>[] = [
  // { value: "sent", label: "Sent" },
  // { value: "cancelled", label: "Cancelled" },
  // { value: "revoked", label: "Revoked" },
  // { value: "declined", label: "Declined" },
  { value: "completed", label: "Signed" },
  // { value: "expired", label: "Expired" },
];

function isSelectableTemplate(
  template: ListCfrTemplateItem,
): template is SelectableManualCfrTemplate {
  return Boolean(template.customer_destination_id && template.DocumentTemplate);
}

export function ManualCfrUploadDialog({
  open,
  onOpenChange,
  onSuccess,
}: ManualCfrUploadDialogProps) {
  const { toast } = useToast();
  const createManualCfrSigningSession = useCreateManualCfrSigningSession();
  const confirmManualCfrSigningSessionUploads =
    useConfirmManualCfrSigningSessionUploads();
  const softDeleteManualCfrSigningSession =
    useSoftDeleteManualCfrSigningSession();
  const templatesResult = useListCfrTemplates({ visibility: "visible" });
  const customersResult = useListCustomers({
    minimal: true,
    fetchInBackground: true,
  });
  const [state, dispatch] = useReducer(
    manualCfrUploadReducer,
    initialManualCfrUploadState,
  );

  const templates = useMemo(() => {
    const rows = templatesResult.data?.data?.data ?? [];
    const selectableRows = rows
      .filter(
        (template: ListCfrTemplateItem): template is SelectableManualCfrTemplate =>
          isSelectableTemplate(template),
      )
      .sort(
        (
          first: SelectableManualCfrTemplate,
          second: SelectableManualCfrTemplate,
        ) => {
          const createdDifference =
            moment(second.created_at).valueOf() - moment(first.created_at).valueOf();
          return createdDifference || second.id.localeCompare(first.id);
        },
      );

    const deduplicatedRows: SelectableManualCfrTemplate[] = [];
    const seenLogicalTemplates = new Set<string>();
    for (const template of selectableRows) {
      const logicalTemplateKey = [
        template.customer_destination_id,
        template.commodity_type,
        String(template.crop_year),
      ].join(":");
      if (!seenLogicalTemplates.has(logicalTemplateKey)) {
        seenLogicalTemplates.add(logicalTemplateKey);
        deduplicatedRows.push(template);
      }
    }

    return deduplicatedRows;
  }, [templatesResult.data]);

  const templateOptions = useMemo(
    (): SelectOptionItem<string>[] =>
      templates.map((template: SelectableManualCfrTemplate) => {
        const commodityLabel =
          CommodityOptions.find(
            (option: (typeof CommodityOptions)[number]) =>
              option.value === template.commodity_type,
          )?.label ?? template.commodity_type;
        const templateTitle =
          template.DocumentTemplate.title || "Untitled CFR template";
        const destination =
          template.Destination?.long_name || "Unknown destination";

        return {
          value: template.id,
          label: `${templateTitle} — ${destination}`,
        };
      }).sort((a, b) => a.label.localeCompare(b.label)),
    [templates],
  );

  const customerOptions = useMemo((): SelectOptionItem<string>[] => {
    const customers = customersResult.data?.data ?? [];
    return customers.map((customer: Customer) => ({
      value: customer.id,
      label: customer.long_name,
    }));
  }, [customersResult.data]);

  const isBusy = ["creating", "uploading", "confirming", "discarding"].includes(
    state.phase,
  );
  const formDisabled = isBusy || state.createResult !== null;

  const resetAndClose = () => {
    dispatch({ type: "RESET" });
    onOpenChange(false);
  };

  const confirmUpload = async (createResult: CreateManualCfrResult) => {
    dispatch({ type: "START_CONFIRM" });
    try {
      const result = await confirmManualCfrSigningSessionUploads({
        signing_session_id: createResult.signing_session_id,
      });
      if (!result.success) {
        dispatch({
          type: "FAIL",
          step: "confirming",
          message: result.message,
        });
        return;
      }

      dispatch({ type: "COMPLETE" });
      toast({
        title: "Manual CFR uploaded",
        description: "Upload confirmed.",
      });
      onSuccess();
      resetAndClose();
    } catch (error: unknown) {
      dispatch({
        type: "FAIL",
        step: "confirming",
        message: getMessageOrDefault(
          error,
          "Failed to confirm the uploaded documents",
        ),
      });
    }
  };

  const uploadDocuments = async (createResult: CreateManualCfrResult) => {
    const finalFile = state.form.final_document;
    const sentFile = state.form.sent_document;
    if (!finalFile) {
      dispatch({
        type: "FAIL",
        step: "uploading",
        message: "The final PDF is no longer available. Discard this upload and try again.",
      });
      return;
    }

    const finalUpload = createResult.uploads.final_document;
    const sentUpload = createResult.uploads.sent_document;
    const uploadItems: UploadItem[] = [
      {
        filename: finalFile.name,
        file: finalFile,
        presignedPutUrl: finalUpload.presigned_put_url,
      },
    ];

    if (sentUpload.id !== finalUpload.id) {
      if (!sentFile) {
        dispatch({
          type: "FAIL",
          step: "uploading",
          message: "The sent PDF is no longer available. Discard this upload and try again.",
        });
        return;
      }
      uploadItems.push({
        filename: sentFile.name,
        file: sentFile,
        presignedPutUrl: sentUpload.presigned_put_url,
      });
    }

    dispatch({
      type: "START_UPLOAD",
      total: uploadItems.length,
      currentFilename: uploadItems[0].filename,
    });

    try {
      for (let index = 0; index < uploadItems.length; index += 1) {
        const uploadItem = uploadItems[index];
        dispatch({
          type: "UPLOAD_PROGRESS",
          done: index,
          currentFilename: uploadItem.filename,
        });
        const uploadResult = await UploadPresignedFile(
          uploadItem.presignedPutUrl,
          uploadItem.file,
          MimeType.Pdf,
        );
        if (!uploadResult.success) {
          dispatch({
            type: "FAIL",
            step: "uploading",
            message: `Failed to upload ${uploadItem.filename}`,
          });
          return;
        }
      }
    } catch (error: unknown) {
      dispatch({
        type: "FAIL",
        step: "uploading",
        message: getMessageOrDefault(error, "Failed to upload the PDF documents"),
      });
      return;
    }

    await confirmUpload(createResult);
  };

  const createTrackingRecord = async () => {
    const formResult = ManualCfrUploadFormValidation.safeParse(state.form);
    if (!formResult.success) {
      dispatch({
        type: "VALIDATION_FAILED",
        message: formResult.error.issues[0]?.message || "Complete all required fields",
      });
      return;
    }

    const selectedTemplate = templates.find(
      (template: SelectableManualCfrTemplate) =>
        template.id === formResult.data.template_id,
    );
    if (!selectedTemplate) {
      dispatch({
        type: "VALIDATION_FAILED",
        message: "The selected template is no longer available",
      });
      return;
    }

    const requestResult = CreateManualCfrRequestValidation.safeParse({
      customer_id: formResult.data.customer_id,
      customer_destination_id: selectedTemplate.customer_destination_id,
      commodity_type: selectedTemplate.commodity_type,
      crop_year: selectedTemplate.crop_year,
      document_title: formResult.data.document_title,
      meta_fields: {
        contract_number: formResult.data.contract_number,
        contract_type: formResult.data.contract_type,
        contract_quantity: formResult.data.contract_quantity,
        contract_shipping_dates: formResult.data.contract_shipping_dates,
        contract_date: formResult.data.contract_date,
      },
      last_status: formResult.data.last_status,
      final_document: { filename: formResult.data.final_document.name },
      sent_document: formResult.data.sent_document
        ? { filename: formResult.data.sent_document.name }
        : undefined,
    });
    if (!requestResult.success) {
      dispatch({
        type: "VALIDATION_FAILED",
        message: requestResult.error.issues[0]?.message || "Invalid upload details",
      });
      return;
    }

    dispatch({ type: "START_CREATE" });
    try {
      const result = await createManualCfrSigningSession(requestResult.data);
      if (!result.success) {
        dispatch({
          type: "FAIL",
          step: "creating",
          message: result.message,
        });
        return;
      }

      dispatch({ type: "CREATE_SUCCEEDED", result: result.data });
      await uploadDocuments(result.data);
    } catch (error: unknown) {
      dispatch({
        type: "FAIL",
        step: "creating",
        message: getMessageOrDefault(error, "Failed to create the tracking record"),
      });
    }
  };

  const discardUpload = async () => {
    if (!state.createResult) {
      return;
    }

    dispatch({ type: "START_DISCARD" });
    try {
      const result = await softDeleteManualCfrSigningSession({
        cfr_document_instance_id: state.createResult.cfr_document_instance_id,
        reason: "Manual upload cancelled before confirmation",
      });
      if (!result.success) {
        dispatch({
          type: "FAIL",
          step: "discarding",
          message: result.message,
        });
        return;
      }
      resetAndClose();
    } catch (error: unknown) {
      dispatch({
        type: "FAIL",
        step: "discarding",
        message: getMessageOrDefault(error, "Failed to discard the manual upload"),
      });
    }
  };

  const retryFailedStep = async () => {
    const failedStep = state.failedStep;
    dispatch({ type: "RETRY" });

    if (failedStep === "creating") {
      await createTrackingRecord();
    } else if (failedStep === "uploading" && state.createResult) {
      await uploadDocuments(state.createResult);
    } else if (failedStep === "confirming" && state.createResult) {
      await confirmUpload(state.createResult);
    } else if (failedStep === "discarding") {
      await discardUpload();
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    if (isBusy || state.createResult) {
      return;
    }
    resetAndClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void createTrackingRecord();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-[850px]"
        onEscapeKeyDown={(event) => {
          if (isBusy) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isBusy) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Upload Manual CFR</DialogTitle>
          <DialogDescription>
            Create a tracked CFR signing record and upload its PDF documents.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4 md:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="manual-cfr-template">Template *</Label>
              <Combobox
                id="manual-cfr-template"
                className="w-full"
                options={templateOptions}
                value={state.form.template_id}
                onValueChange={(value: string) =>
                  dispatch({ type: "PATCH_FORM", patch: { template_id: value } })
                }
                placeholder={templatesResult.isLoading ? "Loading templates..." : "Select template..."}
                disabled={formDisabled}
                dwimOnChange
                isModal
                virtualizeList
                popoverClassName="min-w-[500px] h-[200px]"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="manual-cfr-customer">Customer *</Label>
              <Combobox
                id="manual-cfr-customer"
                className="w-full"
                options={customerOptions}
                value={state.form.customer_id}
                onValueChange={(value: string) =>
                  dispatch({ type: "PATCH_FORM", patch: { customer_id: value } })
                }
                placeholder={customersResult.isLoading ? "Loading customers..." : "Select customer..."}
                disabled={formDisabled}
                dwimOnChange
                isModal
                virtualizeList
                popoverClassName="min-w-[500px] h-[200px]"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="manual-cfr-final-document">Final PDF *</Label>
              <Input
                key={`final-${state.fileInputKey}`}
                id="manual-cfr-final-document"
                type="file"
                accept=".pdf,application/pdf"
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: "PATCH_FORM",
                    patch: { final_document: event.target.files?.[0] ?? null },
                  })
                }
                disabled={formDisabled}
              />
              <p className="text-sm text-muted-foreground">
                The Signed document
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="manual-cfr-sent-document">Sent PDF</Label>
              <Input
                key={`sent-${state.fileInputKey}`}
                id="manual-cfr-sent-document"
                type="file"
                accept=".pdf,application/pdf"
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: "PATCH_FORM",
                    patch: { sent_document: event.target.files?.[0] ?? null },
                  })
                }
                disabled={formDisabled}
              />
              <p className="text-sm text-muted-foreground">
                If omitted, the final PDF is reused as the sent document.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="manual-cfr-document-title">Document title</Label>
              <Input
                id="manual-cfr-document-title"
                value={state.form.document_title}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: "PATCH_FORM",
                    patch: { document_title: event.target.value },
                  })
                }
                disabled={formDisabled}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="manual-cfr-contract-number">Contract number</Label>
              <Input
                id="manual-cfr-contract-number"
                value={state.form.contract_number}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: "PATCH_FORM",
                    patch: { contract_number: event.target.value },
                  })
                }
                disabled={formDisabled}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="manual-cfr-contract-type">Contract type</Label>
              <Input
                id="manual-cfr-contract-type"
                value={state.form.contract_type}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: "PATCH_FORM",
                    patch: { contract_type: event.target.value },
                  })
                }
                disabled={formDisabled}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="manual-cfr-contract-quantity">Contract quantity</Label>
              <Input
                id="manual-cfr-contract-quantity"
                value={state.form.contract_quantity}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: "PATCH_FORM",
                    patch: { contract_quantity: event.target.value },
                  })
                }
                disabled={formDisabled}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="manual-cfr-shipping-dates">Contract shipping dates</Label>
              <Input
                id="manual-cfr-shipping-dates"
                value={state.form.contract_shipping_dates}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: "PATCH_FORM",
                    patch: { contract_shipping_dates: event.target.value },
                  })
                }
                disabled={formDisabled}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="manual-cfr-contract-date">Contract date</Label>
              <Input
                id="manual-cfr-contract-date"
                type="date"
                value={state.form.contract_date}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: "PATCH_FORM",
                    patch: { contract_date: event.target.value },
                  })
                }
                disabled={formDisabled}
              />
            </div>


          </div>

          {state.phase === "creating" && (
            <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              Creating tracking record…
            </div>
          )}
          {state.phase === "uploading" && (
            <div className="mb-4 rounded-md border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900">
              Uploading {state.uploadProgress.done + 1} of {state.uploadProgress.total}: {state.uploadProgress.currentFilename}
            </div>
          )}
          {state.phase === "confirming" && (
            <div className="mb-4 rounded-md border border-indigo-300 bg-indigo-50 p-3 text-sm text-indigo-900">
              Confirming uploaded documents…
            </div>
          )}
          {state.phase === "discarding" && (
            <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              Discarding upload…
            </div>
          )}
          {state.phase === "complete" && (
            <div className="mb-4 rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-900">
              Upload confirmed.
            </div>
          )}
          {state.errorMessage && (
            <div className="mb-4 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {state.errorMessage}
            </div>
          )}

          <DialogFooter>
            {!state.createResult && !isBusy && (
              <Button type="button" variant="outline" onClick={resetAndClose}>
                Cancel
              </Button>
            )}
            {state.phase === "editing" && (
              <Button type="submit">Create and upload</Button>
            )}
            {state.phase === "failed" && (
              <Button type="button" onClick={() => void retryFailedStep()}>
                Retry
              </Button>
            )}
            {state.createResult && state.phase === "failed" && state.failedStep !== "discarding" && (
              <Button type="button" variant="outline" onClick={() => void discardUpload()}>
                Discard upload
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
